/**
 * 换版之后旧标签页里 chunk 404 的自愈入口。
 *
 * 部署是"整包替换"：往 test 分支推一次代码，CI 就用新镜像重建 muou-dev-admin 容器，
 * `/usr/share/nginx/html/assets` 里只剩**这一次**构建的产物。但用户浏览器里开着的
 * 标签页还拿着上一版的 `index-*.js`，其中写死的 chunk 文件名（例如
 * `Home-DHE2jiWv.js`）在新产物里已经不存在了——他在这个标签页里登录、点菜单时，
 * 路由级 `lazy` 的 `import()` 打到 404，react-router 就渲染出
 * "Unexpected Application Error!"。
 *
 * `index.html` 是 `no-cache` 的，所以只要刷新一次就能拿到新入口、自动换到新 chunk
 * 名。这里就做这一件事：Vite 的 preload helper 在依赖加载失败时会在 window 上派发
 * 可取消的 `vite:preloadError`（见产物里的 preload-helper），我们拦下它并刷新一次，
 * 用户看到的是一次短暂的页面重载，而不是一张英文报错页。
 *
 * 为什么要有冷却窗口：**如果产物本身缺文件**（部署不完整、镜像里少了 chunk），
 * 刷新并不能修好，无脑重试会变成无限刷新——那比报错更难退出。所以 10 秒内只自动
 * 刷一次，之后的失败交给 `RouteFailure` 上的按钮，由用户自己决定。
 *
 * 为什么存储不可用时不刷：`sessionStorage` 被禁用时就没有跨页面加载的记忆，
 * 冷却窗口失效，遇到缺文件的产物会一直刷。那种环境下宁可停在兜底页让人点一下。
 */
/** 冷却窗口：这段时间内只自动刷新一次，防止产物本身缺文件时无限刷新 */
export const RELOAD_COOLDOWN_MS = 10_000;

/** 上一次自动刷新的时间戳（`sessionStorage`，跨刷新仍在，同一标签页内有效） */
export const RELOAD_GUARD_KEY = 'muou:chunk-reload-at';

/**
 * 浏览器在动态 import 失败时给出的几种文案。
 *
 * 前两条是 Chromium / Firefox 对 `import()` 网络失败的原话，第三条是 WebKit 的，
 * 第四条是 Vite 预加载 CSS 失败的固定文案。保留它们是为了在**没有** `vite:preloadError`
 * 的路径上（例如我们自己 await 的 `import()`）也能认出这是换版导致的失败，
 * 从而在兜底页上给出"重新载入"而不是"系统错误"。
 */
const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Loading chunk',
];

export interface ChunkRecoveryOptions {
  /** 刷新动作，测试里替换成 spy */
  reload?: () => void;
  /** 当前时间戳，测试里替换成可控时钟 */
  now?: () => number;
  /** 冷却窗口的记录位置，默认 `window.sessionStorage` */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  /** 冷却窗口（毫秒），默认 `RELOAD_COOLDOWN_MS` */
  cooldownMs?: number;
}

/** 这次异常是不是"页面 chunk 没加载下来"（换版后最常见的失败） */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
      ? error.message
      : '';
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function sessionStore(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.sessionStorage;
  } catch {
    // 隐私模式 / 站点数据被禁用时访问 sessionStorage 会抛 SecurityError
    return null;
  }
}

/**
 * 冷却窗口内只放行一次刷新。返回是否真的刷了。
 *
 * 记录写的是"开始刷新的时间"而不是"刷新完成"，因为页面下一秒就没了。
 */
export function reloadOnce({
  reload = () => window.location.reload(),
  now = () => Date.now(),
  storage = sessionStore(),
  cooldownMs = RELOAD_COOLDOWN_MS,
}: ChunkRecoveryOptions = {}): boolean {
  if (!storage) return false;

  const at = now();
  // 注意 Number(null) === 0：没记录时不能当成"1970 年刷过"，否则首屏永远落在冷却窗口里
  const raw = storage.getItem(RELOAD_GUARD_KEY);
  const previous = raw === null ? Number.NaN : Number(raw);
  if (Number.isFinite(previous) && at - previous < cooldownMs) return false;

  try {
    storage.setItem(RELOAD_GUARD_KEY, String(at));
  } catch {
    // 配额满等写入失败：本条记录不了就放弃自动刷新，避免无记忆地反复刷
    return false;
  }
  reload();
  return true;
}

/** 用户主动点的"重新载入"：清掉冷却记录后无条件刷新 */
export function reloadNow(): void {
  try {
    sessionStore()?.setItem(RELOAD_GUARD_KEY, String(0));
  } catch {
    // 清不掉也照刷：用户是手动点的，不存在无人值守的循环
  }
  window.location.reload();
}

/**
 * 接管 `vite:preloadError`：换版导致的 chunk 404 直接刷新到新版本。
 *
 * 只在应用入口调用一次，返回解绑函数（测试用）。
 */
export function installChunkRecovery(
  options: ChunkRecoveryOptions = {},
): () => void {
  const onPreloadError = (event: Event) => {
    if (reloadOnce(options)) {
      // 正在刷新：取消默认行为，别让同一次失败再渲染一屏报错页闪一下
      event.preventDefault();
      return;
    }
    // 冷却窗口内不刷新，**不要** preventDefault：取消之后 Vite 会把错误吞掉，
    // react-router 拿到的是一个没有意义的 "reading 'default'"，
    // RouteFailure 就认不出这是换版，只能说"页面加载失败"。放行原始错误，
    // 兜底页才能给出"系统已更新，请重新载入"这句真正能解决问题的文案。
    console.warn(
      '[chunkRecovery] chunk 加载失败且在冷却窗口内，已跳过自动刷新，交给兜底页',
    );
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
